import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';

import { loadElasticSearchResults } from '@modules/professional/api/load-elastic-search-results.server';
import { getProjectsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-project';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import { DEFAULT_PROJECT_PAGE_SIZE } from '@modules/professional/page/loaders/projects-page-loader.server';
import { DEFAULT_PRODUCT_PAGE_SIZE } from '@modules/professional/page/loaders/our-products-page-loader.server';
import {
    ARTICLES_PAGE_SIZE,
    NETWORK_PAGE_SIZE,
    mapArticleTilesFromSearch,
    mapNetworkProfessionalsFromSearch,
} from '@modules/professional/util/map-es-tab-results';
import type { SearchIndex } from '~/modules/search/type';

export const PROFESSIONAL_TAB_LOAD_MORE_ACTION =
    '/actions/professional-tab-load-more';

const URL_SEGMENT_PATTERN = /^[\w-]+$/;

const PAGE_SIZE_BY_TYPE: Partial<Record<SearchIndex, number>> = {
    project: DEFAULT_PROJECT_PAGE_SIZE,
    product: DEFAULT_PRODUCT_PAGE_SIZE,
    professional: NETWORK_PAGE_SIZE,
    blogpost: ARTICLES_PAGE_SIZE,
};

const isSupportedType = (
    value: string | null,
): value is 'project' | 'product' | 'professional' | 'blogpost' =>
    value === 'project' ||
    value === 'product' ||
    value === 'professional' ||
    value === 'blogpost';

type ESSearchResult = Awaited<
    ReturnType<typeof loadElasticSearchResults>
>['searchResult'];
type RequestUser = LoaderFunctionArgs['context']['user'];

/** Map an ES search result to the tile shape the requested tab renders. */
const mapTilesForType = (
    searchType: 'project' | 'product' | 'professional' | 'blogpost',
    searchResult: ESSearchResult,
    user: RequestUser,
) => {
    switch (searchType) {
        case 'project':
            return getProjectsFromESSearchResults(searchResult, user).map(
                (model) => ({ aspectRatio: 'default' as const, model }),
            );
        case 'product':
            return getProductsFromESSearchResults(searchResult, user).map(
                (model) => ({ aspectRatio: 'square' as const, model }),
            );
        case 'professional':
            return mapNetworkProfessionalsFromSearch(searchResult);
        case 'blogpost':
            return mapArticleTilesFromSearch(searchResult, user);
    }
};

/**
 * "View More" / infinite-scroll endpoint for the professional Projects,
 * Products, Network and Articles tabs. Reuses {@link loadElasticSearchResults}
 * so the professional scoping, predefined filters and ShowOwnProjectsOnly logic
 * stay identical to the initial tab loaders — the caller forwards the tab's
 * current filter querystring plus the target `page`.
 *
 * Query params: `urlSegment`, `searchType` (`project` | `product` |
 * `professional` | `blogpost`), `page`, and the tab's active filter params
 * (sort, viewProjectsBy, facets, in-page search).
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger, user } = context;
    const url = new URL(request.url);
    const urlSegment = url.searchParams.get('urlSegment');
    const searchType = url.searchParams.get('searchType');

    if (
        !urlSegment ||
        !URL_SEGMENT_PATTERN.test(urlSegment) ||
        !isSupportedType(searchType)
    ) {
        logger.warning('Invalid params in professional-tab-load-more', {
            urlSegment,
            searchType,
        });
        return routerData({ tiles: [], hasMore: false, totalCount: 0 });
    }

    try {
        const { searchResult, pagination } = await loadElasticSearchResults({
            context,
            request,
            urlSegment,
            searchType,
            size: PAGE_SIZE_BY_TYPE[searchType] ?? DEFAULT_PRODUCT_PAGE_SIZE,
        });

        const tiles = mapTilesForType(searchType, searchResult, user);

        return routerData({
            tiles,
            hasMore: pagination.page < pagination.totalPages,
            totalCount: pagination.totalCount,
        });
    } catch (error) {
        // `createPaginationState` throws a redirect when the requested page is
        // out of range (e.g. the tab shrank, or ES re-ranking left the tail
        // empty). For this on-demand endpoint that simply means "no more
        // results" — answer cleanly so the client stops rather than treating a
        // benign redirect as a server error.
        if (error instanceof Response) {
            return routerData({ tiles: [], hasMore: false, totalCount: 0 });
        }
        logger.error('Error in /actions/professional-tab-load-more', {
            error,
            urlSegment,
            searchType,
        });
        return routerData(
            { tiles: [], hasMore: false, totalCount: 0 },
            { status: 500 },
        );
    }
};

/** Data is fetched on demand; never revalidate on navigation. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
